// src/pages/VendaComparativo.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api.js";
import Swal from 'sweetalert2';
import Menu from "../../comum/Menu/menu.jsx";
import Footer from "../../comum/Footer/index.jsx";
import "./vendacomparativo.css";

import {
  FaUndoAlt,
  FaCalendarAlt,
  FaFilter,
  FaExchangeAlt,
} from "react-icons/fa";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// ================= FORMATADORES E UTILITÁRIOS FORA DO COMPONENTE =================
const formatMoeda = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 }).format(v ?? 0);

const formatPercent = (v) =>
  isNaN(v) || v === null || v === undefined ? "0,00%" : `${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const formatLoja = (n) => (n ? String(n).padStart(2, '0') : "");

const formatarMesExibicao = (mesAno) => {
  if (!mesAno || mesAno.length !== 4) return mesAno;
  return `${mesAno.slice(2)}/20${mesAno.slice(0, 2)}`;
};

const formatarParaInput = (mesAno) => {
  if (!mesAno || mesAno.length !== 4) return "";
  return `20${mesAno.slice(0, 2)}-${mesAno.slice(2)}`;
};

const calcularHomologo = (mesAno) => {
  if (!mesAno || mesAno.length !== 4) return "";
  const aa = parseInt(mesAno.slice(0, 2), 10);
  const mm = mesAno.slice(2);
  const anoHomologo = String(aa - 1).padStart(2, '0');
  return `${anoHomologo}${mm}`;
};

const NORM_DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const obterDiaSemanaNome = (numDiaStr, mesAnoStr) => {
  if (!numDiaStr || !mesAnoStr || mesAnoStr.length !== 4) return "";
  
  const dia = parseInt(numDiaStr, 10);
  const ano = parseInt(`20${mesAnoStr.slice(0, 2)}`, 10);
  const mes = parseInt(mesAnoStr.slice(2), 10) - 1;

  const date = new Date(ano, mes, dia);
  if (isNaN(date.getTime())) return "";
  
  return NORM_DIAS_SEMANA[date.getDay()];
};

const MetaProgressoGarrafa = ({ percentual }) => {
  const pct = Math.min(Math.max(percentual || 0, 0), 100);
  return (
    <div className="bottle-wrapper">
      <div className="bottle-cap"></div>
      <div className="bottle-container" title={`Atingido: ${pct.toFixed(1)}%`}>
        <div className="bottle-fill" style={{ height: `${pct}%` }}></div>
      </div>
      <span className="bottle-badge">{pct.toFixed(1)}%</span>
    </div>
  );
};

function ModalGenerico({ show, onClose, title, children }) {
  if (!show) return null;
  return (
    <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(15, 23, 42, 0.75)', zIndex: 1200, backdropFilter: 'blur(4px)' }}>
      <div className="modal-dialog modal-md modal-dialog-centered">
        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '14px', overflow: 'hidden' }}>
          <div className="modal-header bg-custom-red text-white py-3 px-4">
            <h5 className="modal-title font-weight-bold fs-6 m-0 text-white">{title}</h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body p-4">{children}</div>
          <div className="modal-footer bg-light py-2 px-4">
            <button type="button" className="btn btn-custom-red btn-sm px-4 fw-semibold" onClick={onClose}>Fechar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChartZoomModal({ show, onClose, title, children }) {
  if (!show) return null;
  return (
    <div className="chart-zoom-modal-overlay">
      <div className="chart-zoom-modal-content">
        <div className="chart-zoom-modal-header">
          <h5 className="m-0 fs-6 fw-bold text-dark">{title}</h5>
          <button type="button" className="btn-close" onClick={onClose}></button>
        </div>
        <div className="chart-zoom-modal-body">
          {children}
        </div>
      </div>
    </div>
  );
}

function LoadingModal({ show, message }) {
  if (!show) return null;
  return (
    <div className="loading-modal-overlay">
      <div className="loading-modal-content">
        <div className="spinner-border text-custom-red" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
          <span className="visually-hidden">A carregar...</span>
        </div>
        <div className="loading-text mt-3 fw-bold text-secondary">{message}</div>
      </div>
    </div>
  );
}

// ================= COMPONENTE PRINCIPAL =================
const VendaComparativo = () => {
  const [showFiltroModal, setShowFiltroModal] = useState(false);
  const [zoomChart, setZoomChart] = useState(null);
  const [activeTabMobile, setActiveTabMobile] = useState('dia');

  const [lojas, setLojas] = useState([]);
  const [lojaSelecionada, setLojaSelecionada] = useState("");
  
  const [mes1, setMes1] = useState("");
  const [mes2, setMes2] = useState("");
  const [isHomologo, setIsHomologo] = useState(true);

  const [dadosMes1, setDadosMes1] = useState([]);
  const [dadosMes2, setDadosMes2] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("A carregar dados...");

  const navigate = useNavigate();
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    const usuario = localStorage.getItem("chapa");
    const permiteVendas = localStorage.getItem("permiteVendas");

    if (!usuario) {
      navigate("/arbor/login");
      return;
    }

    if (permiteVendas !== "S") {
      Swal.fire({
        icon: 'error',
        title: 'Acesso Negado',
        text: 'Não é permitido acessar as vendas.',
        confirmButtonText: 'OK'
      }).then(() => {
        navigate("/arbor/home");
      });
    }
  }, [navigate]);

  useEffect(() => {
    const hoje = new Date();
    const aa = String(hoje.getFullYear()).slice(2);
    const mm = String(hoje.getMonth() + 1).padStart(2, "0");
    const m1 = `${aa}${mm}`;
    setMes1(m1);
    setMes2(calcularHomologo(m1));
  }, []);

  useEffect(() => {
    const fetchLojas = async () => {
      try {
        const { data } = await api.get('/lojas/localizacao');
        setLojas(data || []);
      } catch (e) {
        console.error("Erro ao carregar lojas:", e);
        setLojas([]);
      }
    };
    fetchLojas();
  }, []);

  // Busca Vendas dos Dois Períodos
  const carregarComparativo = useCallback(async () => {
    const usuario = localStorage.getItem("chapa");
    if (!mes1 || !mes2 || !usuario) return;

    setLoading(true);
    setLoadingMessage(
      lojaSelecionada 
        ? `A carregar comparativo da Loja ${lojaSelecionada}...` 
        : `A carregar comparativo TOTAL (Todas as Lojas)...`
    );

    try {
      const params1 = { mes: mes1, usuario, ...(lojaSelecionada ? { busca: lojaSelecionada } : {}) };
      const params2 = { mes: mes2, usuario, ...(lojaSelecionada ? { busca: lojaSelecionada } : {}) };

      const [res1, res2] = await Promise.all([
        api.get(`venda/mes/acum`, { params: params1 }),
        api.get(`venda/mes/acum`, { params: params2 })
      ]);

      let d1 = Array.isArray(res1.data) ? res1.data : [];
      let d2 = Array.isArray(res2.data) ? res2.data : [];

      // 🔹 CORREÇÃO: VERIFICA SE QUALQUER UM DOS MESES É O MÊS ATUAL
      const hoje = new Date();
      const aaAtual = String(hoje.getFullYear()).slice(2);
      const mmAtual = String(hoje.getMonth() + 1).padStart(2, "0");
      const mesAtualRef = `${aaAtual}${mmAtual}`;

      // Dia limite de corte: Dia anterior ao dia de hoje (D - 1)
      const diaLimite = Math.max(1, hoje.getDate() - 1);

      const ehMesAtualP1 = mes1 === mesAtualRef;
      const ehMesAtualP2 = mes2 === mesAtualRef;

      // Se P1 ou P2 for o mês atual, limitamos os dados até o dia anterior (D - 1)
      if (ehMesAtualP1 || ehMesAtualP2) {
        d1 = d1.filter(item => parseInt(item.numdia, 10) <= diaLimite);
        d2 = d2.filter(item => parseInt(item.numdia, 10) <= diaLimite);
      }

      setDadosMes1(d1);
      setDadosMes2(d2);
    } catch (e) {
      console.error("Erro ao carregar comparativo:", e);
      Swal.fire('Erro', 'Não foi possível carregar os dados comparativos.', 'error');
      setDadosMes1([]);
      setDadosMes2([]);
    } finally {
      setLoading(false);
    }
  }, [mes1, mes2, lojaSelecionada]);

  useEffect(() => {
    if (mes1 && mes2) {
      carregarComparativo();
    }
  }, [carregarComparativo]);

  const handleMes1Change = (valorInput) => {
    if (!valorInput) return;
    const [ano, mm] = valorInput.split("-");
    const aa = ano.slice(2);
    const novoM1 = `${aa}${mm}`;
    setMes1(novoM1);
    if (isHomologo) {
      setMes2(calcularHomologo(novoM1));
    }
  };

  const handleMes2Change = (valorInput) => {
    if (!valorInput) return;
    const [ano, mm] = valorInput.split("-");
    const aa = ano.slice(2);
    setMes2(`${aa}${mm}`);
    setIsHomologo(false);
  };

  const toggleHomologo = (checked) => {
    setIsHomologo(checked);
    if (checked && mes1) {
      setMes2(calcularHomologo(mes1));
    }
  };

  const totalDias = Math.max(dadosMes1.length, dadosMes2.length);
  const combinedData = Array.from({ length: totalDias }, (_, i) => {
    const d1 = dadosMes1[i] || {};
    const d2 = dadosMes2[i] || {};
    const numdia = d1.numdia || d2.numdia || String(i + 1).padStart(2, '0');

    return {
      numdia,
      venda1: d1.venda || 0,
      meta1: d1.meta || 0,
      margem1: d1.margem || 0,
      atingido1: d1.meta > 0 ? (d1.venda / d1.meta) * 100 : (d1.atingido || 0),
      atingidoper1: d1.atingidoper || 0,
      venda2: d2.venda || 0,
      meta2: d2.meta || 0,
      margem2: d2.margem || 0,
      atingido2: d2.meta > 0 ? (d2.venda / d2.meta) * 100 : (d2.atingido || 0),
      atingidoper2: d2.atingidoper || 0,
    };
  });

  // TOTAIS
  const totalVenda1 = dadosMes1.reduce((acc, curr) => acc + (curr.venda || 0), 0);
  const totalMeta1 = dadosMes1.reduce((acc, curr) => acc + (curr.meta || 0), 0);
  const totalVlrMargem1 = dadosMes1.reduce((acc, curr) => acc + ((curr.venda || 0) * ((curr.margem || 0) / 100)), 0);
  const totalAtingido1 = totalMeta1 > 0 ? (totalVenda1 / totalMeta1) * 100 : 0;
  const totalMargem1 = totalVenda1 > 0 ? (totalVlrMargem1 / totalVenda1) * 100 : 0;
  const totalAtingidoPer1 = dadosMes1.length > 0 ? (dadosMes1[dadosMes1.length - 1].atingidoper || 0) : 0;

  const totalVenda2 = dadosMes2.reduce((acc, curr) => acc + (curr.venda || 0), 0);
  const totalMeta2 = dadosMes2.reduce((acc, curr) => acc + (curr.meta || 0), 0);
  const totalVlrMargem2 = dadosMes2.reduce((acc, curr) => acc + ((curr.venda || 0) * ((curr.margem || 0) / 100)), 0);
  const totalAtingido2 = totalMeta2 > 0 ? (totalVenda2 / totalMeta2) * 100 : 0;
  const totalMargem2 = totalVenda2 > 0 ? (totalVlrMargem2 / totalVenda2) * 100 : 0;
  const totalAtingidoPer2 = dadosMes2.length > 0 ? (dadosMes2[dadosMes2.length - 1].atingidoper || 0) : 0;

  // 🔹 CÁLCULO AGRUPADO DE DIA DA SEMANA (VENDA, META E MARGEM PONDERADA)
  const semanaMap1 = { 
    Dom: { venda: 0, meta: 0, vlrMargem: 0 }, 
    Seg: { venda: 0, meta: 0, vlrMargem: 0 }, 
    Ter: { venda: 0, meta: 0, vlrMargem: 0 }, 
    Qua: { venda: 0, meta: 0, vlrMargem: 0 }, 
    Qui: { venda: 0, meta: 0, vlrMargem: 0 }, 
    Sex: { venda: 0, meta: 0, vlrMargem: 0 }, 
    Sáb: { venda: 0, meta: 0, vlrMargem: 0 } 
  };

  const semanaMap2 = { 
    Dom: { venda: 0, meta: 0, vlrMargem: 0 }, 
    Seg: { venda: 0, meta: 0, vlrMargem: 0 }, 
    Ter: { venda: 0, meta: 0, vlrMargem: 0 }, 
    Qua: { venda: 0, meta: 0, vlrMargem: 0 }, 
    Qui: { venda: 0, meta: 0, vlrMargem: 0 }, 
    Sex: { venda: 0, meta: 0, vlrMargem: 0 }, 
    Sáb: { venda: 0, meta: 0, vlrMargem: 0 } 
  };

  (dadosMes1 || []).forEach(d => {
    const nomeSemana = obterDiaSemanaNome(d.numdia, mes1);
    if (nomeSemana && semanaMap1[nomeSemana]) {
      const vda = d.venda || 0;
      semanaMap1[nomeSemana].venda += vda;
      semanaMap1[nomeSemana].meta += (d.meta || 0);
      semanaMap1[nomeSemana].vlrMargem += (vda * ((d.margem || 0) / 100));
    }
  });

  (dadosMes2 || []).forEach(d => {
    const nomeSemana = obterDiaSemanaNome(d.numdia, mes2);
    if (nomeSemana && semanaMap2[nomeSemana]) {
      const vda = d.venda || 0;
      semanaMap2[nomeSemana].venda += vda;
      semanaMap2[nomeSemana].meta += (d.meta || 0);
      semanaMap2[nomeSemana].vlrMargem += (vda * ((d.margem || 0) / 100));
    }
  });

  const labelP1 = `${formatarMesExibicao(mes1)}`;
  const labelP2 = `${formatarMesExibicao(mes2)}`;

  // 🔹 DADOS EM PERCENTUAL (%) SOBRE O TOTAL DE CADA PERÍODO
  const chartSemanaData = {
    labels: NORM_DIAS_SEMANA,
    datasets: [
      {
        type: 'bar',
        label: `Venda (${labelP1})`,
        data: NORM_DIAS_SEMANA.map(dia => totalVenda1 > 0 ? (semanaMap1[dia].venda / totalVenda1) * 100 : 0),
        backgroundColor: 'rgba(2, 132, 199, 0.85)',
        borderColor: 'rgba(2, 132, 199, 1)',
        borderWidth: 1,
        borderRadius: 4,
        detalhesSemana: NORM_DIAS_SEMANA.map(dia => ({
          venda: semanaMap1[dia].venda,
          meta: semanaMap1[dia].meta,
          margem: semanaMap1[dia].venda > 0 ? (semanaMap1[dia].vlrMargem / semanaMap1[dia].venda) * 100 : 0
        }))
      },
      {
        type: 'bar',
        label: `Venda (${labelP2})`,
        data: NORM_DIAS_SEMANA.map(dia => totalVenda2 > 0 ? (semanaMap2[dia].venda / totalVenda2) * 100 : 0),
        backgroundColor: 'rgba(245, 158, 11, 0.85)',
        borderColor: 'rgba(245, 158, 11, 1)',
        borderWidth: 1,
        borderRadius: 4,
        detalhesSemana: NORM_DIAS_SEMANA.map(dia => ({
          venda: semanaMap2[dia].venda,
          meta: semanaMap2[dia].meta,
          margem: semanaMap2[dia].venda > 0 ? (semanaMap2[dia].vlrMargem / semanaMap2[dia].venda) * 100 : 0
        }))
      }
    ]
  };

  // CHART 1: Atingido Dia
  const chartData1 = {
    labels: combinedData.map(d => `Dia ${d.numdia}`),
    datasets: [
      {
        type: 'bar',
        label: `% Atingido (${labelP1})`,
        data: combinedData.map(d => d.atingido1),
        backgroundColor: 'rgba(2, 132, 199, 0.85)',
        borderColor: 'rgba(2, 132, 199, 1)',
        borderWidth: 1,
        borderRadius: 4
      },
      {
        type: 'bar',
        label: `% Atingido (${labelP2})`,
        data: combinedData.map(d => d.atingido2),
        backgroundColor: 'rgba(245, 158, 11, 0.85)',
        borderColor: 'rgba(245, 158, 11, 1)',
        borderWidth: 1,
        borderRadius: 4
      }
    ]
  };

  const getChartOptions1 = (isZoom = false) => ({
    responsive: true,
    maintainAspectRatio: !isZoom,
    layout: { padding: { top: 25 } },
    plugins: {
      legend: { position: 'top', labels: { font: { size: isZoom ? 12 : 10, weight: '600' } } },
      title: { display: true, text: `Venda x Meta Dia (% Atingido: ${labelP1} vs ${labelP2})`, font: { size: isZoom ? 15 : 11, weight: 'bold' }, color: '#0f172a' },
      tooltip: {
        backgroundColor: '#0f172a',
        padding: 8,
        callbacks: {
          label: (ctx) => {
            const idx = ctx.dataIndex;
            const item = combinedData[idx];
            if (ctx.datasetIndex === 0) {
              return [
                `Período ${labelP1}:`,
                `Venda: ${formatMoeda(item.venda1)}`,
                `Meta: ${formatMoeda(item.meta1)}`,
                `Atingido: ${formatPercent(item.atingido1)}`
              ];
            }
            return [
              `Período ${labelP2}:`,
              `Venda: ${formatMoeda(item.venda2)}`,
              `Meta: ${formatMoeda(item.meta2)}`,
              `Atingido: ${formatPercent(item.atingido2)}`
            ];
          }
        }
      },
      datalabels: {
        color: '#0f172a',
        font: { weight: 'bold', size: isZoom ? 10 : (isMobile ? 6 : 7) },
        anchor: 'end',
        align: 'top',
        offset: 2,
        rotation: -90,
        formatter: (v) => `${(v ?? 0).toFixed(1)}%`
      }
    },
    scales: {
      x: { ticks: { font: { size: isZoom ? 10 : 7.5 } }, grid: { display: false } },
      y: { beginAtZero: true, max: 130, title: { display: true, text: 'Atingido (%)', font: { weight: '600', size: 9 } }, ticks: { callback: (v) => v + '%', font: { size: 8 } } }
    }
  });

  // CHART 2: Margem
  const chartDataMargem = {
    labels: combinedData.map(d => `Dia ${d.numdia}`),
    datasets: [
      {
        type: 'line',
        label: `Margem ${labelP1} (%)`,
        data: combinedData.map(d => d.margem1),
        borderColor: '#0284c7',
        backgroundColor: '#0284c7',
        borderWidth: 2,
        pointRadius: 3,
        tension: 0.3
      },
      {
        type: 'line',
        label: `Margem ${labelP2} (%)`,
        data: combinedData.map(d => d.margem2),
        borderColor: '#f59e0b',
        backgroundColor: '#f59e0b',
        borderWidth: 2,
        borderDash: [4, 4],
        pointRadius: 3,
        tension: 0.3
      }
    ]
  };

  const getChartOptionsMargem = (isZoom = false) => ({
    responsive: true,
    maintainAspectRatio: !isZoom,
    layout: { padding: { top: 25 } },
    plugins: {
      legend: { position: 'top', labels: { font: { size: isZoom ? 12 : 10, weight: '600' } } },
      title: { display: true, text: `Evolução da Margem Real (%: ${labelP1} vs ${labelP2})`, font: { size: isZoom ? 15 : 11, weight: 'bold' }, color: '#0f172a' },
      tooltip: { backgroundColor: '#0f172a', padding: 8 },
      datalabels: {
        color: (ctx) => (ctx.datasetIndex === 0 ? '#0369a1' : '#b45309'),
        font: { weight: 'bold', size: isZoom ? 10 : (isMobile ? 6.5 : 7.5) },
        anchor: (ctx) => (ctx.datasetIndex === 0 ? 'end' : 'start'),
        align: (ctx) => (ctx.datasetIndex === 0 ? 'top' : 'bottom'),
        offset: 4,
        formatter: (v) => `${(v ?? 0).toFixed(1)}%`
      }
    },
    scales: {
      x: { ticks: { font: { size: isZoom ? 10 : 7.5 } }, grid: { display: false } },
      y: { beginAtZero: true, max: 50, ticks: { callback: (v) => v + '%', font: { size: 8 } } }
    }
  });

  // CHART 3: Atingido Período
  const chartDataAcum = {
    labels: combinedData.map(d => `Dia ${d.numdia}`),
    datasets: [
      {
        type: 'bar',
        label: `% Acumulado (${labelP1})`,
        data: combinedData.map(d => d.atingidoper1),
        backgroundColor: 'rgba(16, 185, 129, 0.85)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 1,
        borderRadius: 4
      },
      {
        type: 'bar',
        label: `% Acumulado (${labelP2})`,
        data: combinedData.map(d => d.atingidoper2),
        backgroundColor: 'rgba(239, 68, 68, 0.85)',
        borderColor: 'rgba(239, 68, 68, 1)',
        borderWidth: 1,
        borderRadius: 4
      }
    ]
  };

  const getChartOptionsAcum = (isZoom = false) => ({
    responsive: true,
    maintainAspectRatio: !isZoom,
    layout: { padding: { top: 25 } },
    plugins: {
      legend: { position: 'top', labels: { font: { size: isZoom ? 12 : 10, weight: '600' } } },
      title: { display: true, text: `Meta Atingida por Período (%: ${labelP1} vs ${labelP2})`, font: { size: isZoom ? 15 : 11, weight: 'bold' }, color: '#0f172a' },
      tooltip: { backgroundColor: '#0f172a', padding: 8 },
      datalabels: {
        color: '#0f172a',
        font: { weight: 'bold', size: isZoom ? 10 : (isMobile ? 6 : 7.5) },
        anchor: 'end',
        align: 'top',
        offset: 2,
        rotation: -90,
        formatter: (v) => `${(v ?? 0).toFixed(1)}%`
      }
    },
    scales: {
      x: { ticks: { font: { size: isZoom ? 10 : 7.5 } }, grid: { display: false } },
      y: { beginAtZero: true, max: 130, ticks: { callback: (v) => v + '%', font: { size: 8 } } }
    }
  });

  // 🔹 CHART 4: OPÇÕES AJUSTADAS COM VALORES NAS LEGENDAS E % NAS BARRAS
  const getChartOptionsSemana = (isZoom = false) => ({
    responsive: true,
    maintainAspectRatio: !isZoom,
    layout: { padding: { top: 25 } },
    plugins: {
      legend: { position: 'top', labels: { font: { size: isZoom ? 12 : 10, weight: '600' } } },
      title: { display: true, text: `Participação Venda por Dia da Semana (%: ${labelP1} vs ${labelP2})`, font: { size: isZoom ? 15 : 11, weight: 'bold' }, color: '#0f172a' },
      tooltip: {
        backgroundColor: '#0f172a',
        padding: 10,
        callbacks: {
          title: (items) => `Dia da Semana: ${items[0].label}`,
          label: (ctx) => {
            const detalhe = ctx.dataset.detalhesSemana ? ctx.dataset.detalhesSemana[ctx.dataIndex] : null;
            if (!detalhe) return `${ctx.dataset.label}: ${ctx.raw.toFixed(1)}%`;

            return [
              `${ctx.dataset.label}:`,
              `  • Part. Venda: ${ctx.raw.toFixed(1)}%`,
              `  • Venda: ${formatMoeda(detalhe.venda)}`,
              `  • Meta: ${formatMoeda(detalhe.meta)}`,
              `  • Margem: ${detalhe.margem.toFixed(2)}%`
            ];
          }
        }
      },
      datalabels: {
        color: '#0f172a',
        font: { weight: 'bold', size: isZoom ? 9 : (isMobile ? 6.5 : 7.5) },
        anchor: 'end',
        align: 'top',
        offset: 2,
        rotation: -90,
        formatter: (v) => (v > 0 ? `${v.toFixed(1)}%` : "")
      }
    },
    scales: {
      x: { ticks: { font: { size: isZoom ? 10 : 8 } }, grid: { display: false } },
      y: { beginAtZero: true, title: { display: true, text: 'Participação (%)', font: { weight: '600', size: 9 } }, ticks: { callback: (v) => v + "%", font: { size: 8 } } }
    }
  });

  return (
    <div className="flash-vendas-layout-wrapper">
      <Menu modulo="ARBOR" />

      <LoadingModal show={loading} message={loadingMessage} />

      <button className="btn-floating-calendar" onClick={() => setShowFiltroModal(true)} title="Configurar Comparativo">
        <FaCalendarAlt />
      </button>

      <div className="container-fluid px-3 px-md-4 max-width-container">
        
        {/* CABEÇALHO DA PÁGINA */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pt-2 gap-2">
          <h5 className="fw-bold m-0 text-dark fs-6">
            Comparativo de Vendas - Loja: <span className="text-primary">{lojaSelecionada ? `Loja ${lojaSelecionada}` : 'TOTAL (Empresa)'}</span> | <span className="text-primary">{labelP1}</span> <FaExchangeAlt className="mx-1 text-muted fs-7" /> <span className="text-warning">{labelP2}</span>
          </h5>

          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-outline-primary btn-sm fw-semibold" onClick={() => setShowFiltroModal(true)}>
              <FaFilter className="me-1" /> Filtros
            </button>
            <button className="btn btn-outline-secondary btn-sm fw-semibold" onClick={carregarComparativo} disabled={loading}>
              <FaUndoAlt className="me-1" /> Atualizar
            </button>
          </div>
        </div>

        {/* VISÃO DESKTOP: 4 GRÁFICOS EM GRID */}
        {!isMobile && combinedData.length > 0 && (
          <div className="row g-2 mb-4">
            <div className="col-md-3">
              <div className="chart-container position-relative h-100">
                <button
                  className="btn btn-expand-chart"
                  onClick={() => setZoomChart({ type: 1, title: `Venda x Meta Dia (${labelP1} vs ${labelP2})` })}
                  title="Ampliar Gráfico"
                >
                  🔍 Expandir
                </button>
                <div style={{ width: "100%", height: 260 }}>
                  <Bar options={getChartOptions1(false)} data={chartData1} />
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="chart-container position-relative h-100">
                <button
                  className="btn btn-expand-chart"
                  onClick={() => setZoomChart({ type: 2, title: `Evolução da Margem Real (${labelP1} vs ${labelP2})` })}
                  title="Ampliar Gráfico"
                >
                  🔍 Expandir
                </button>
                <div style={{ width: "100%", height: 260 }}>
                  <Line options={getChartOptionsMargem(false)} data={chartDataMargem} />
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="chart-container position-relative h-100">
                <button
                  className="btn btn-expand-chart"
                  onClick={() => setZoomChart({ type: 3, title: `Meta Atingida por Período (${labelP1} vs ${labelP2})` })}
                  title="Ampliar Gráfico"
                >
                  🔍 Expandir
                </button>
                <div style={{ width: "100%", height: 260 }}>
                  <Bar options={getChartOptionsAcum(false)} data={chartDataAcum} />
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="chart-container position-relative h-100">
                <button
                  className="btn btn-expand-chart"
                  onClick={() => setZoomChart({ type: 4, title: `Participação Venda por Dia da Semana (${labelP1} vs ${labelP2})` })}
                  title="Ampliar Gráfico"
                >
                  🔍 Expandir
                </button>
                <div style={{ width: "100%", height: 260 }}>
                  <Bar options={getChartOptionsSemana(false)} data={chartSemanaData} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* VISÃO MOBILE: GRÁFICO ÚNICO COM ABAS ABAIXO */}
        {isMobile && combinedData.length > 0 && (
          <div className="mb-4 pt-1">
            <div className="chart-container position-relative">
              {activeTabMobile === 'dia' && (
                <>
                  <button
                    className="btn btn-expand-chart"
                    onClick={() => setZoomChart({ type: 1, title: `Venda x Meta Dia (${labelP1} vs ${labelP2})` })}
                  >
                    🔍
                  </button>
                  <div style={{ width: "100%", height: 250 }}>
                    <Bar options={getChartOptions1(false)} data={chartData1} />
                  </div>
                </>
              )}

              {activeTabMobile === 'margem' && (
                <>
                  <button
                    className="btn btn-expand-chart"
                    onClick={() => setZoomChart({ type: 2, title: `Evolução da Margem Real (${labelP1} vs ${labelP2})` })}
                  >
                    🔍
                  </button>
                  <div style={{ width: "100%", height: 250 }}>
                    <Line options={getChartOptionsMargem(false)} data={chartDataMargem} />
                  </div>
                </>
              )}

              {activeTabMobile === 'periodo' && (
                <>
                  <button
                    className="btn btn-expand-chart"
                    onClick={() => setZoomChart({ type: 3, title: `Meta Atingida por Período (${labelP1} vs ${labelP2})` })}
                  >
                    🔍
                  </button>
                  <div style={{ width: "100%", height: 250 }}>
                    <Bar options={getChartOptionsAcum(false)} data={chartDataAcum} />
                  </div>
                </>
              )}

              {activeTabMobile === 'semana' && (
                <>
                  <button
                    className="btn btn-expand-chart"
                    onClick={() => setZoomChart({ type: 4, title: `Participação Venda por Dia da Semana (${labelP1} vs ${labelP2})` })}
                  >
                    🔍
                  </button>
                  <div style={{ width: "100%", height: 250 }}>
                    <Bar options={getChartOptionsSemana(false)} data={chartSemanaData} />
                  </div>
                </>
              )}
            </div>

            {/* BARRA DE ABAS MOBILE */}
            <ul className="nav nav-pills nav-justified bg-white p-1 rounded-3 shadow-sm border mt-2">
              <li className="nav-item">
                <button
                  className={`nav-link btn-sm fw-bold py-1 ${activeTabMobile === 'dia' ? 'active bg-primary text-white' : 'text-dark'}`}
                  onClick={() => setActiveTabMobile('dia')}
                >
                  Meta Dia
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link btn-sm fw-bold py-1 ${activeTabMobile === 'margem' ? 'active bg-primary text-white' : 'text-dark'}`}
                  onClick={() => setActiveTabMobile('margem')}
                >
                  Margem
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link btn-sm fw-bold py-1 ${activeTabMobile === 'periodo' ? 'active bg-primary text-white' : 'text-dark'}`}
                  onClick={() => setActiveTabMobile('periodo')}
                >
                  Período
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link btn-sm fw-bold py-1 ${activeTabMobile === 'semana' ? 'active bg-primary text-white' : 'text-dark'}`}
                  onClick={() => setActiveTabMobile('semana')}
                >
                  Dia Semana
                </button>
              </li>
            </ul>
          </div>
        )}

        {/* RESUMO GERAL MOBILE */}
        {isMobile && combinedData.length > 0 && (
          <div className="card mb-3 border shadow-sm rounded-3 bg-white p-3">
            <div className="small text-uppercase text-muted text-center fw-bold mb-2 pb-1 border-bottom">
              Resumo Geral do Comparativo
            </div>
            
            {/* Bloco Período 1 */}
            <div className="bg-light-blue p-2 rounded-3 border mb-2">
              <span className="x-small fw-bold text-primary d-block mb-1">{labelP1} (Atual)</span>
              <div className="row text-center g-1 x-small">
                <div className="col-4 border-end">Venda: <strong className="text-dark d-block">{formatMoeda(totalVenda1)}</strong></div>
                <div className="col-4 border-end">Ating.: <strong className={`d-block ${totalAtingido1 >= 100 ? 'text-success' : 'text-danger'}`}>{formatPercent(totalAtingido1)}</strong></div>
                <div className="col-4">Margem: <strong className="text-dark d-block">{formatPercent(totalMargem1)}</strong></div>
              </div>
            </div>

            {/* Bloco Período 2 */}
            <div className="bg-light-amber p-2 rounded-3 border">
              <span className="x-small fw-bold text-amber d-block mb-1">{labelP2} (Comparativo)</span>
              <div className="row text-center g-1 x-small">
                <div className="col-4 border-end">Venda: <strong className="text-dark d-block">{formatMoeda(totalVenda2)}</strong></div>
                <div className="col-4 border-end">Ating.: <strong className={`d-block ${totalAtingido2 >= 100 ? 'text-success' : 'text-danger'}`}>{formatPercent(totalAtingido2)}</strong></div>
                <div className="col-4">Margem: <strong className="text-dark d-block">{formatPercent(totalMargem2)}</strong></div>
              </div>
            </div>
          </div>
        )}

        {/* VISÃO DESKTOP (Tabela Comparativa) */}
        {!isMobile && (
          <div className="table-frozen-wrapper shadow-sm border rounded-3 mb-4">
            <table className="table table-striped table-hover align-middle m-0 table-compact-fixed">
              <thead className="table-header-frozen">
                <tr>
                  <th style={{ width: '50px' }}>Dia</th>
                  <th style={{ width: '100px' }}>Período</th>
                  <th className="text-end" style={{ width: '120px' }}>Venda</th>
                  <th className="text-end" style={{ width: '120px' }}>Meta</th>
                  <th className="text-end" style={{ width: '90px' }}>Margem</th>
                  <th className="text-end" style={{ width: '100px' }}>Ating. Dia</th>
                  <th className="text-end" style={{ width: '100px' }}>Ating. Período</th>
                  <th className="text-center" style={{ width: '80px' }}>Progresso</th>
                </tr>
              </thead>
              <tbody>
                {/* LINHA DE TOTAL NO TOPO */}
                {combinedData.length > 0 && (
                  <>
                    <tr className="fw-bold table-secondary border-bottom">
                      <td rowSpan="2" className="align-middle text-center text-primary">TOTAL</td>
                      <td className="text-primary">{labelP1}</td>
                      <td className="text-end text-primary">{formatMoeda(totalVenda1)}</td>
                      <td className="text-end">{formatMoeda(totalMeta1)}</td>
                      <td className="text-end text-dark">{formatPercent(totalMargem1)}</td>
                      <td className={`text-end ${totalAtingido1 >= 100 ? "text-success" : "text-custom-red"}`}>
                        {formatPercent(totalAtingido1)}
                      </td>
                      <td className={`text-end ${totalAtingidoPer1 >= 100 ? "text-success" : "text-custom-red"}`}>
                        {formatPercent(totalAtingidoPer1)}
                      </td>
                      <td className="text-center py-1">
                        <MetaProgressoGarrafa percentual={totalAtingidoPer1} />
                      </td>
                    </tr>
                    <tr className="fw-bold table-secondary border-bottom">
                      <td className="text-amber">{labelP2}</td>
                      <td className="text-end text-amber">{formatMoeda(totalVenda2)}</td>
                      <td className="text-end">{formatMoeda(totalMeta2)}</td>
                      <td className="text-end text-dark">{formatPercent(totalMargem2)}</td>
                      <td className={`text-end ${totalAtingido2 >= 100 ? "text-success" : "text-custom-red"}`}>
                        {formatPercent(totalAtingido2)}
                      </td>
                      <td className={`text-end ${totalAtingidoPer2 >= 100 ? "text-success" : "text-custom-red"}`}>
                        {formatPercent(totalAtingidoPer2)}
                      </td>
                      <td className="text-center py-1">
                        <MetaProgressoGarrafa percentual={totalAtingidoPer2} />
                      </td>
                    </tr>
                  </>
                )}

                {/* LINHAS DIÁRIAS GRUPADAS */}
                {combinedData.map((d) => (
                  <React.Fragment key={d.numdia}>
                    <tr>
                      <td rowSpan="2" className="align-middle text-center fw-bold border-end bg-light">
                        Dia {d.numdia}
                      </td>
                      <td className="fw-semibold text-primary">{labelP1}</td>
                      <td className="text-end">{formatMoeda(d.venda1)}</td>
                      <td className="text-end">{formatMoeda(d.meta1)}</td>
                      <td className="text-end text-dark">{formatPercent(d.margem1)}</td>
                      <td className={`text-end fw-semibold ${d.atingido1 >= 100 ? "text-success" : "text-custom-red"}`}>
                        {formatPercent(d.atingido1)}
                      </td>
                      <td className={`text-end ${d.atingidoper1 >= 100 ? "text-success" : "text-custom-red"}`}>
                        {formatPercent(d.atingidoper1)}
                      </td>
                      <td className="text-center py-1">
                        <MetaProgressoGarrafa percentual={d.atingidoper1} />
                      </td>
                    </tr>

                    <tr className="border-bottom">
                      <td className="fw-semibold text-amber">{labelP2}</td>
                      <td className="text-end">{formatMoeda(d.venda2)}</td>
                      <td className="text-end">{formatMoeda(d.meta2)}</td>
                      <td className="text-end text-dark">{formatPercent(d.margem2)}</td>
                      <td className={`text-end fw-semibold ${d.atingido2 >= 100 ? "text-success" : "text-custom-red"}`}>
                        {formatPercent(d.atingido2)}
                      </td>
                      <td className={`text-end ${d.atingidoper2 >= 100 ? "text-success" : "text-custom-red"}`}>
                        {formatPercent(d.atingidoper2)}
                      </td>
                      <td className="text-center py-1">
                        <MetaProgressoGarrafa percentual={d.atingidoper2} />
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* VISÃO MOBILE */}
        {isMobile && (
          <div className="mobile-list pb-5">
            {combinedData.map((d) => (
              <div key={d.numdia} className="card mb-3 border-0 shadow-sm rounded-3 overflow-hidden">
                <div className="card-header bg-white py-2 px-3 border-bottom d-flex justify-content-between align-items-center">
                  <span className="fw-bold text-dark fs-6">DIA {d.numdia}</span>
                </div>
                <div className="p-2 bg-light">
                  <div className="bg-white p-2 rounded-3 border mb-2 shadow-xs">
                    <div className="d-flex justify-content-between align-items-center fw-bold text-primary small border-bottom pb-1 mb-1">
                      <span>{labelP1}</span>
                      <span className={d.atingido1 >= 100 ? "text-success" : "text-custom-red"}>
                        {formatPercent(d.atingido1)}
                      </span>
                    </div>
                    <div className="row g-1 x-small text-muted text-center">
                      <div className="col-6 border-end">Venda: <b className="text-dark">{formatMoeda(d.venda1)}</b></div>
                      <div className="col-6">Meta: <b className="text-dark">{formatMoeda(d.meta1)}</b></div>
                      <div className="col-12 border-top pt-1 mt-1">Margem: <b className="text-dark">{formatPercent(d.margem1)}</b></div>
                    </div>
                  </div>

                  <div className="bg-white p-2 rounded-3 border shadow-xs">
                    <div className="d-flex justify-content-between align-items-center fw-bold text-amber small border-bottom pb-1 mb-1">
                      <span>{labelP2}</span>
                      <span className={d.atingido2 >= 100 ? "text-success" : "text-custom-red"}>
                        {formatPercent(d.atingido2)}
                      </span>
                    </div>
                    <div className="row g-1 x-small text-muted text-center">
                      <div className="col-6 border-end">Venda: <b className="text-dark">{formatMoeda(d.venda2)}</b></div>
                      <div className="col-6">Meta: <b className="text-dark">{formatMoeda(d.meta2)}</b></div>
                      <div className="col-12 border-top pt-1 mt-1">Margem: <b className="text-dark">{formatPercent(d.margem2)}</b></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>

      {/* MODAL FILTRO */}
      <ModalGenerico
        show={showFiltroModal}
        onClose={() => setShowFiltroModal(false)}
        title="Configurar Comparativo"
      >
        <div className="mb-3">
          <label className="form-label fw-bold text-muted small">Selecione a Loja:</label>
          <select
            className="form-select rounded-3"
            value={lojaSelecionada}
            onChange={(e) => setLojaSelecionada(e.target.value)}
          >
            <option value="">TODAS AS LOJAS (Empresa Total)</option>
            {lojas.map(loja => {
              const cod = formatLoja(loja.cod_emp);
              return (
                <option key={loja.cod_emp} value={cod}>
                  Filial {cod} - {loja.razao}
                </option>
              );
            })}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold text-muted small">Período Principal (P1):</label>
          <input 
            type="month" 
            className="form-control rounded-3" 
            value={formatarParaInput(mes1)}
            onChange={(e) => handleMes1Change(e.target.value)} 
          />
        </div>

        <div className="form-check form-switch mb-3">
          <input 
            className="form-check-input cursor-pointer" 
            type="checkbox" 
            id="checkHomologo" 
            checked={isHomologo} 
            onChange={(e) => toggleHomologo(e.target.checked)} 
          />
          <label className="form-check-label small fw-semibold text-dark cursor-pointer" htmlFor="checkHomologo">
            Comparar com Período Homólogo (-1 Ano)
          </label>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold text-muted small">Período Comparativo (P2):</label>
          <input 
            type="month" 
            className="form-control rounded-3" 
            disabled={isHomologo}
            value={formatarParaInput(mes2)}
            onChange={(e) => handleMes2Change(e.target.value)} 
          />
        </div>

        <div className="mt-4 pt-2 border-top text-end">
          <button className="btn btn-custom-red btn-sm px-4 fw-semibold" onClick={() => {
            setShowFiltroModal(false);
            carregarComparativo();
          }}>
            Aplicar Comparativo
          </button>
        </div>
      </ModalGenerico>

      {/* MODAL ZOOM GRÁFICOS */}
      <ChartZoomModal
        show={!!zoomChart}
        onClose={() => setZoomChart(null)}
        title={zoomChart?.title || 'Gráfico Comparativo'}
      >
        {zoomChart?.type === 1 && (
          <Bar options={getChartOptions1(true)} data={chartData1} />
        )}
        {zoomChart?.type === 2 && (
          <Line options={getChartOptionsMargem(true)} data={chartDataMargem} />
        )}
        {zoomChart?.type === 3 && (
          <Bar options={getChartOptionsAcum(true)} data={chartDataAcum} />
        )}
        {zoomChart?.type === 4 && (
          <Bar options={getChartOptionsSemana(true)} data={chartSemanaData} />
        )}
      </ChartZoomModal>

      <Footer className="footer navbar-fixed-bottom" />
    </div>
  );
};

export default VendaComparativo;